// The Station releases screen's server state (spec/14 §12, F-STN-12, spec/10 §8), over the generated client —
// `packages/contracts/src/routes/station-releases.ts`:
//
//   GET   /v1/hq/station-releases              every release, newest first              hq:releases
//   POST  /v1/hq/station-releases              register a signed release manifest       hq:releases
//   PATCH /v1/hq/station-releases/{version}    halt / resume one release                hq:releases
//   GET   /v1/hq/station-rollouts              each channel's rollout                   hq:releases
//   PUT   /v1/hq/station-rollouts/{channel}    target version, percentage, halt         hq:releases
//   GET   /v1/hq/station-fleet                 the fleet's client versions and states   hq:releases
//
//   GET   /v1/hq/boot-server-releases                    boot server releases, and each channel's current  hq:releases
//   POST  /v1/hq/boot-server-releases                    register a signed boot server release            hq:releases
//   PUT   /v1/hq/boot-server-release-channels/{channel}  which boot server release is current on it       hq:releases
//
// All need `hq:releases` and a second factor. The console never signs anything: a release is registered from
// the manifest the offline release tool signed, and the server verifies that signature before storing it.

import type {
  BootServerRelease,
  BootServerReleaseChannel,
  BootServerReleaseChannelState,
  BootServerReleaseList,
  SetStationRollout,
  StationFleet,
  StationRelease,
  StationReleaseChannel,
  StationRollout,
} from '@clavian/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApi } from '../../api/client';

export const releaseKeys = {
  all: ['hq', 'station-releases'] as const,
  releases: ['hq', 'station-releases', 'releases'] as const,
  rollouts: ['hq', 'station-releases', 'rollouts'] as const,
  fleet: ['hq', 'station-releases', 'fleet'] as const,
  boot: ['hq', 'boot-server-releases'] as const,
};

/** Every registered release, newest first. */
export function useStationReleases() {
  const api = useApi();
  return useQuery({
    queryKey: releaseKeys.releases,
    queryFn: async (): Promise<readonly StationRelease[]> => (await api.listStationReleases()).data,
  });
}

/** Each channel's rollout, `stable` then `beta`. */
export function useStationRollouts() {
  const api = useApi();
  return useQuery({
    queryKey: releaseKeys.rollouts,
    queryFn: async (): Promise<readonly StationRollout[]> => (await api.listStationRollouts()).data,
  });
}

/** The fleet's client versions and updater states — counts only. Refreshed every minute while open. */
export function useStationFleet() {
  const api = useApi();
  return useQuery({
    queryKey: releaseKeys.fleet,
    queryFn: (): Promise<StationFleet> => api.getStationFleet(),
    refetchInterval: 60_000,
  });
}

/** Register a signed manifest: `409` when the version is registered, `422` when the signature does not verify. */
export function useRegisterStationRelease() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<StationRelease, unknown, string>({
    mutationFn: (signedManifest) => api.registerStationRelease({ body: { signedManifest } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: releaseKeys.releases });
    },
  });
}

/** Halt a release (offered to no station) or resume it. */
export function useSetStationReleaseHalted() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<StationRelease, unknown, { version: string; halted: boolean }>({
    mutationFn: ({ version, halted }) => api.updateStationRelease({ path: { version }, body: { halted } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: releaseKeys.releases });
    },
  });
}

/** Replace one channel's rollout; `422` when the target is not a registered release. */
export function useSetStationRollout() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<StationRollout, unknown, { channel: StationReleaseChannel; body: SetStationRollout }>({
    mutationFn: ({ channel, body }) => api.setStationRollout({ path: { channel }, body }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: releaseKeys.rollouts });
    },
  });
}

// ---------------------------------------------------------------------------------------------
// Boot server releases (manual updates of the Clavian Boot server)
// ---------------------------------------------------------------------------------------------

/** Every registered boot server release, newest first, and the release current on each channel. */
export function useBootServerReleases() {
  const api = useApi();
  return useQuery({
    queryKey: releaseKeys.boot,
    queryFn: (): Promise<BootServerReleaseList> => api.listBootServerReleases(),
  });
}

/** Register a signed boot server manifest: `409` when the version is registered, `422` when it does not verify. */
export function useRegisterBootServerRelease() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<BootServerRelease, unknown, string>({
    mutationFn: (signedManifest) => api.registerBootServerRelease({ body: { signedManifest } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: releaseKeys.boot });
    },
  });
}

/** Make a registered boot server release current on a channel (or none); `422` when it is not registered. */
export function useSetBootServerReleaseChannel() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<BootServerReleaseChannelState, unknown, { channel: BootServerReleaseChannel; currentVersion: string | null }>({
    mutationFn: ({ channel, currentVersion }) => api.setBootServerReleaseChannel({ path: { channel }, body: { currentVersion } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: releaseKeys.boot });
    },
  });
}
